import Config from '@config';
import { CACHE_EXPIRATION_TIME } from '@gasbi/shared/common/features/constants';
import {
  FiltersGrid,
  Pagination,
  infiniteQueryOptions,
  normalizeInfiniteQueryData,
} from '@gasbi/shared/index';
import { createApi } from '@reduxjs/toolkit/query/react';

import axiosBaseQuery from '@/utils/axiosBaseQuery';

import {
  GetResourcePnlArgs,
  GetResourcePnlMonthArgs,
  Resource,
  ResourcePnl,
  ResourcePnlMonth,
  ResourcePnlSummary,
  ResourcePsgCost,
  ResourcesMonthArgs,
} from './types';

export const resourcesApi = createApi({
  reducerPath: 'resourcesApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${Config.VITE_API_URL}/resources`,
  }),
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  tagTypes: ['Resource', 'Resources'],
  endpoints: builder => ({
    getResourcesI: builder.infiniteQuery<
      Resource[],
      FiltersGrid & ResourcesMonthArgs,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: `/`,
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['Resource', 'Resources'],
    }),
    getResourcePsgCostsI: builder.infiniteQuery<
      ResourcePsgCost[],
      FiltersGrid & ResourcesMonthArgs,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: `/psg-costs`,
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['Resource', 'Resources'],
    }),
    // There is no GET /resources/{id}; the list is Gridify-filterable on every projected field,
    // so one lot is a page of one filtered by id.
    getResourceById: builder.query<Resource | undefined, string>({
      query: id => ({
        url: `/`,
        params: { filter: `id=${id}`, page: 1, pageSize: 1 },
      }),
      transformResponse: (items: Resource[]) => items[0],
      providesTags: (_, __, id) => [{ type: 'Resource', id }],
    }),
    getResourcePnl: builder.query<ResourcePnl, GetResourcePnlArgs>({
      query: ({ resourceId, from, to }) => ({
        url: `/${resourceId}/pnl`,
        params: {
          ...(from ? { from } : {}),
          ...(to ? { to } : {}),
        },
      }),
      providesTags: (_, __, { resourceId }) => [
        { type: 'Resource', id: resourceId },
      ],
    }),
    getResourcePnlSummary: builder.query<ResourcePnlSummary, string>({
      query: id => ({ url: `/${id}/pnl/summary` }),
      providesTags: (_, __, id) => [{ type: 'Resource', id }],
    }),
    getResourcePnlMonth: builder.query<
      ResourcePnlMonth,
      GetResourcePnlMonthArgs
    >({
      query: ({ resourceId, year, month }) => ({
        url: `/${resourceId}/pnl/month`,
        params: { year, month },
      }),
      providesTags: (_, __, { resourceId }) => [
        { type: 'Resource', id: resourceId },
      ],
    }),
  }),
});

export const {
  useGetResourceByIdQuery,
  useGetResourcePnlQuery,
  useGetResourcePnlMonthQuery,
  useGetResourcePnlSummaryQuery,
} = resourcesApi;

export const useGetResourcesIInfiniteQuery = normalizeInfiniteQueryData(
  resourcesApi.useGetResourcesIInfiniteQuery,
);

export const useGetResourcePsgCostsIInfiniteQuery = normalizeInfiniteQueryData(
  resourcesApi.useGetResourcePsgCostsIInfiniteQuery,
);
